import React, { useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { Modal, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useDivisionUsers } from '../../hooks/use-division-users';
import httpClient from '../../services/http-client';

interface AddApproversModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  divisionId: string;
  currentStep: number;
  totalSteps: number;
  onSuccess?: () => void;
}

export function AddApproversModal({
  isOpen,
  onClose,
  orderId,
  divisionId,
  currentStep,
  totalSteps,
  onSuccess
}: AddApproversModalProps) {
  const [selectedStep, setSelectedStep] = useState(currentStep);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [approverEmails, setApproverEmails] = useState<string>('');
  const [notifyApprovers, setNotifyApprovers] = useState(true);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch users for the division
  const { data: usersResponse } = useDivisionUsers(divisionId, {
    search: searchQuery,
    limit: 50,
    offset: 0
  });
  const users = usersResponse?.users || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Parse emails from textarea
    const emailList = approverEmails
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (selectedUserIds.length === 0 && emailList.length === 0) {
      setError('Please select at least one user or enter email addresses');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for adding approvers');
      return;
    }

    setIsSubmitting(true);

    try {
      await httpClient.post(
        `/api/v1/divisions/${divisionId}/orders/${orderId}/add-approvers`,
        {
          stepLevel: selectedStep,
          approverIds: selectedUserIds,
          approverEmails: emailList,
          notifyApprovers,
          reason: reason.trim()
        }
      );

      handleClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'An error occurred while adding approvers');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedStep(currentStep);
    setSelectedUserIds([]);
    setApproverEmails('');
    setReason('');
    setError(null);
    setSearchQuery('');
    onClose();
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectedUsers = users.filter(user => selectedUserIds.includes(user.id));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Approvers to Order"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950 p-4 border border-red-200 dark:border-red-800">
            <div className="flex items-start">
              <i className="fa-solid fa-circle-exclamation text-red-600 dark:text-red-400 mt-0.5 mr-2"></i>
              <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="rounded-md bg-blue-50 dark:bg-blue-950 p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start">
            <i className="fa-solid fa-circle-info text-blue-600 dark:text-blue-400 mt-0.5 mr-2"></i>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Add additional approvers to this order</p>
              <p>You can add users from your division or invite approvers by email address.</p>
            </div>
          </div>
        </div>

        {/* Step Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
            Approval Step
          </label>
          <select
            value={selectedStep}
            onChange={(e) => setSelectedStep(parseInt(e.target.value))}
            className="w-full rounded-md border border-gray-300 dark:border-secondary-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            required
          >
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
              <option key={step} value={step}>
                Step {step} {step === currentStep ? '(Current)' : step < currentStep ? '(Completed)' : '(Future)'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-secondary-400">
            Select which approval step to add approvers to
          </p>
        </div>

        {/* User Search and Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
            Select Users from Division
          </label>
          
          {/* Search Input */}
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-gray-300 dark:border-secondary-600 px-3 py-2 mb-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          />

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="mb-3 p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg border border-gray-200 dark:border-secondary-700">
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(user => (
                  <Badge key={user.id} variant="info" className="flex items-center gap-1">
                    <span>{user.firstName} {user.lastName}</span>
                    <button
                      type="button"
                      onClick={() => toggleUserSelection(user.id)}
                      className="ml-1 hover:bg-blue-700 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* User List */}
          <div className="border border-gray-200 dark:border-secondary-700 rounded-lg max-h-48 overflow-y-auto">
            {users.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-secondary-400">
                {searchQuery ? 'No users found' : 'Start typing to search users'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-secondary-700">
                {users.map(user => {
                  const isSelected = selectedUserIds.includes(user.id);
                  return (
                    <div
                      key={user.id}
                      className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700 ${isSelected ? 'bg-blue-50 dark:bg-blue-950' : ''}`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-secondary-400">{user.email}</p>
                        </div>
                        {isSelected && (
                          <i className="fa-solid fa-check text-blue-600 dark:text-blue-400"></i>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Email Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
            Or Add by Email Address
          </label>
          <textarea
            value={approverEmails}
            onChange={(e) => setApproverEmails(e.target.value)}
            placeholder="Enter email addresses (comma or newline separated)&#10;example@company.com, another@company.com"
            rows={3}
            className="w-full rounded-md border border-gray-300 dark:border-secondary-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-secondary-400">
            Separate multiple email addresses with commas or new lines
          </p>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
            Reason for Adding Approvers <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Additional oversight required for high-value order"
            rows={3}
            required
            className="w-full rounded-md border border-gray-300 dark:border-secondary-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          />
        </div>

        {/* Notify Approvers Checkbox */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="notifyApprovers"
            checked={notifyApprovers}
            onChange={(e) => setNotifyApprovers(e.target.checked)}
            className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          />
          <label htmlFor="notifyApprovers" className="ml-2 block text-sm text-gray-700 dark:text-secondary-200">
            Send email notifications to new approvers
          </label>
        </div>

        {/* Summary */}
        <div className="rounded-md bg-gray-50 dark:bg-secondary-900 p-4 border border-gray-200 dark:border-secondary-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Summary</p>
          <ul className="text-sm text-gray-700 dark:text-secondary-200 space-y-1">
            <li>• Adding to Step {selectedStep} of {totalSteps}</li>
            <li>• {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected from division</li>
            <li>• {approverEmails.split(/[,\n]/).filter(e => e.trim()).length} email address{approverEmails.split(/[,\n]/).filter(e => e.trim()).length !== 1 ? 'es' : ''} entered</li>
            <li>• Notifications: {notifyApprovers ? 'Enabled' : 'Disabled'}</li>
          </ul>
        </div>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                Adding Approvers...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Approvers
              </>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
